import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import ConfirmDialog from 'components/common/ConfirmDialog';
import CodeInput from 'components/common/CodeInput';
import QrScanner from 'components/dashboard/healthRecordsShare/QrScanner';
import ImportFromDocumentsDialog from 'components/dashboard/healthRecords/ImportFromDocumentsDialog';
import {
  normalizeManualCode,
  openHospitalRecord,
  parseScannedShareCode,
} from 'services/healthRecordsShareService';
import {
  applyProposals,
  buildProposals,
  partnerRecordToExtraction,
} from 'services/healthRecordImportService';

/**
 * A hospital sent this patient their record through the partner API
 * (`recipient: "patient"`). Same scan-or-type entry as ChartMind's
 * ConsumeShareCodeDialog; a hospital link arrives with the code already filled in.
 *
 * The code works once, so the current record is read BEFORE it is spent: a failed
 * read must not burn the code. Then every item goes through the same review the
 * document import uses, and only what the patient ticks is saved.
 *
 * SANDBOX DEMO ONLY: the hub shows the entry point only when isDevEnvironment().
 */
const ImportHospitalRecordDialog = ({ open, initialCode, onClose, onImported }) => {
  const { t } = useTranslation('masterchart');
  const { user, medications, addMedication } = useContext(AuthContext);
  const [mode, setMode] = useState('scan');
  const [code, setCode] = useState('');
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prefilled = normalizeManualCode(initialCode);
    setMode(prefilled ? 'code' : 'scan');
    setCode(prefilled);
    setError(null);
    setOpened(null);
    setSaveError(null);
  }, [open, initialCode]);

  const submitCode = useCallback(
    async (rawCode) => {
      const cleanCode = normalizeManualCode(rawCode);
      if (cleanCode.length !== 7 || !user?.uid) {
        setError(t('hospitalImport.fullCode'));
        return;
      }
      setOpening(true);
      setError(null);
      try {
        const snap = await db.collection('users').doc(user.uid).get();
        const healthRecords = snap.data()?.healthRecords || {};
        const { record, patientName, sourceName } = await openHospitalRecord({
          manualCode: cleanCode,
        });
        const extraction = partnerRecordToExtraction(record, patientName);
        setOpened({
          sourceName,
          healthRecords,
          extraction,
          proposals: buildProposals(extraction, healthRecords, medications),
        });
      } catch (err) {
        setError(err?.message || t('hospitalImport.openError'));
      } finally {
        setOpening(false);
      }
    },
    [user?.uid, medications, t],
  );

  const handleScan = useCallback(
    (raw) => {
      const extracted = parseScannedShareCode(raw);
      if (!extracted) {
        setError(t('hospitalImport.notOurCode'));
        return;
      }
      setCode(normalizeManualCode(extracted));
      setMode('code');
      submitCode(extracted);
    },
    [submitCode, t],
  );

  const handleApply = async (approved) => {
    if (!user?.uid || !approved.length) return;
    setSaving(true);
    setSaveError(null);
    try {
      const merged = applyProposals(approved, opened.healthRecords);
      await db.collection('users').doc(user.uid).set({ healthRecords: merged }, { merge: true });
      for (const p of approved.filter((a) => a.field === 'medications')) {
        await addMedication(p.value);
      }
      onImported?.(
        { key: 'hospitalImport.added', values: { count: approved.length, source: opened.sourceName } },
      );
      onClose();
    } catch (err) {
      setSaveError(err?.message || t('hospitalImport.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const digitCount = normalizeManualCode(code).length;

  return (
    <>
      <Dialog open={open && !opened} onClose={opening ? undefined : onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('hospitalImport.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('hospitalImport.intro')}
            </Typography>
            <Tabs
              value={mode}
              onChange={(_, v) => {
                setMode(v);
                setError(null);
              }}
              variant="fullWidth"
            >
              <Tab value="scan" label={t('hospitalImport.scanTab')} />
              <Tab value="code" label={t('hospitalImport.codeTab')} />
            </Tabs>

            {mode === 'scan' ? (
              <QrScanner active={open && !opened && mode === 'scan'} onResult={handleScan} />
            ) : (
              <CodeInput
                value={code}
                onChange={(digits) => {
                  setCode(digits);
                  if (error) setError(null);
                }}
                onComplete={(digits) => {
                  if (!opening) submitCode(digits);
                }}
                error={Boolean(error)}
                disabled={opening}
                autoFocus
                ariaLabel={t('hospitalImport.codeLabel')}
              />
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={opening}>
            {t('common.cancel')}
          </Button>
          {mode === 'code' && (
            <LoadingButton
              variant="contained"
              onClick={() => submitCode(code)}
              loading={opening}
              disabled={digitCount !== 7}
            >
              {t('hospitalImport.import')}
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>

      <ImportFromDocumentsDialog
        open={open && Boolean(opened)}
        onClose={() => setConfirmDiscard(true)}
        loading={false}
        error={saveError}
        extraction={opened?.extraction}
        proposals={opened?.proposals}
        saving={saving}
        onApply={handleApply}
        sourceName={opened?.sourceName}
      />

      <ConfirmDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title={t('hospitalImport.discardTitle')}
        message={t('hospitalImport.discardMessage')}
        confirmLabel={t('hospitalImport.discard')}
        cancelLabel={t('common.cancel')}
        confirmColor="error"
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
      />
    </>
  );
};

export default ImportHospitalRecordDialog;
